import type { HelpTopic } from '../types';

/** Guides for the HR, Recruitment, Payroll and Billing screens. */
export const peopleHelp: HelpTopic[] = [
  // ── HR ─────────────────────────────────────────────────────────────────────
  {
    path: ['/employees', '/employees/:employeeId'],
    title: 'Employees',
    summary:
      'The staff list. Open an employee to see and edit their details, reset their password, and manage leave balance, documents, letters and exit.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Choose Active Employees or In-Active Employees at the top. An employee with a leaving date is In-Active.',
          'Columns: Employee ID, Employee name, Contact, Email ID, Department, Designation, Employment Period and More details. Click a column heading to sort.',
          'Click view in More details. The employee’s page opens below the list.',
          'The employee’s page shows the details card, Salary Structure, Payslips, LEAVE BALANCE, Documents, Letters & certificates and Exit & clearance.',
        ],
      },
      {
        title: 'Step by step: edit an employee',
        steps: [
          'Click view on the employee’s row.',
          'Click Edit (top right of the details).',
          'Change the fields you need (see Fields).',
          'Click Save. A green message "Employee details updated" confirms it.',
        ],
      },
      {
        title: 'Fields (Edit dialog)',
        fields: [
          { name: 'Employee name', help: 'Full name, up to 100 characters. This is also the name on the login.', required: true },
          { name: 'Gender', help: 'Male, Female or Other. Professional tax slabs depend on it.' },
          { name: 'Contact number', help: '10 digits, no +91 or spaces.' },
          { name: 'Email ID', help: 'A valid email like name@example.com.' },
          { name: 'Designation', help: 'Job title, e.g. Junior Engineer.' },
          { name: 'Department', help: 'Choose from the list.' },
          { name: 'Aadhaar number', help: '12 digits. Cannot start with 0 or 1.' },
          { name: 'PAN number', help: '10 characters like ABCDE1234F. Changed to capital letters by itself.' },
          { name: 'Nationality', help: 'e.g. Indian.' },
          { name: 'Medical condition (if any)', help: 'Leave blank if none.' },
          { name: 'Current salary', help: 'Monthly salary in rupees, numbers only. For payroll, set the salary in Pay Profiles (with an effective date).' },
        ],
      },
      {
        title: 'Step by step: reset a password',
        steps: [
          'Open the employee and click Reset Password.',
          'Click Reset Password again to confirm. The old password stops working.',
          'Click Copy in the next box and share the User name and new password with the employee safely.',
          'The password is shown only once. If you close the box without copying, reset again.',
        ],
      },
      {
        title: 'Documents and exit',
        steps: [
          'Documents: click Upload (or Replace) on a document row. Click Choose file, fill Issued on, Expires on (blank if it does not expire) and Notes. Click File document.',
          'Exit: in Exit & clearance click Start exit. Fill Exit type, Resignation date, Last working day, Notice days and Reason. Click Save.',
          'When the exit interview, asset return, knowledge transfer and dues are done, tick all four boxes. Fill Full & final amount (₹) and F&F paid on.',
          'Click Complete exit and confirm. The employee gets a leaving date and moves to In-Active Employees.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Employment Status on the details card saves as soon as you pick it (needs the Edit action on Employees). For someone who is leaving, also record the leaving date in Exit & clearance.',
          'Complete exit needs all four clearance boxes ticked and a Last working day. After completion the exit cannot be edited. The exit type is saved as Terminated for Terminated, and as Resigned for all other types.',
          'Payroll stops paying the employee after the last working day.',
          'Salary Structure is only an estimate (PT ₹200, TDS 10%). The real monthly salary is in Salary Processing.',
          'Documents shows "On file" (green), "Missing" or "Expired" (red). Uploading again replaces the earlier copy.',
          'Issue letter on the employee page opens the same dialog as Letters & Certificates, with the employee already chosen.',
          'If the list does not load, your login role may not have HR access — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Pay Profiles', path: '/pay-profiles' },
      { label: 'Letters & Certificates', path: '/letters-certificates' },
      { label: 'Departments', path: '/departments' },
    ],
  },
  {
    path: '/admin-module',
    title: 'Vouchers & Leaves (HR desk)',
    summary:
      'One page with two lists: staff reimbursement (voucher) requests and leave applications, to approve or reject. You can also add leave for an employee.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Vouchers & Office Supplies Requests: Date, Name, Department, Request type and Amount. Click a row to see Applied on and the employee’s Note.',
          'LEAVES: Name, Department, Leave type, Start date and End date (with First Half / Second Half under the date). Click a row to see the number of days, Applied on and the Leave reason.',
          'In each list choose Active (waiting) or Closed. Under Closed, choose Approved or Rejected.',
          'Status colours: Pending orange, Approved green, Rejected red.',
        ],
      },
      {
        title: 'Step by step: approve or reject',
        steps: [
          'Stay on Active. Click the row to read the details.',
          'Click Approve to approve.',
          'Or click Reject, type the Rejection Reason (3 to 300 characters) and click Save.',
          'A green message confirms it. The request moves to Closed.',
        ],
      },
      {
        title: 'Step by step: add leave for an employee',
        steps: [
          'Click + Add Leave for an employee.',
          'Choose the Employee and the Leave type.',
          'Choose the Start date and its session: Session 1 (First Half of the day), Session 2 (Second Half of the day) or Full Day.',
          'For more than one day, choose the End date and its session.',
          'Type the Reason (3 to 600 characters). Click Save.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Leave added here is approved at once and is paid leave. It goes straight into attendance and payroll.',
          'The start date cannot be in the past. For a past day, correct the day in Attendance Register instead.',
                    'Approving a voucher creates the payment record under Payments → Reimbursements.',
          'If you don’t see Approve and Reject, your role doesn’t have the approve action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Leave Approvals', path: '/leave-approvals' },
      { label: 'Reimbursement', path: '/reimbursement-approvals' },
    ],
  },
  {
    path: '/employee-records',
    title: 'Employee Records',
    summary:
      'The raw employee table: link a user login to a new employee record, or correct position, department, salary and personal data.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Columns: ID, Employee ID, Position, Department, Joined, Status, Salary and Actions (pencil = Edit Employee, red bin = Delete Employee).',
          'There is no name column. Use Employees to find a person by name.',
        ],
      },
      {
        title: 'Step by step: add an employee record',
        steps: [
          'First create the person’s login on Users, and note its user ID.',
          'Click Add Employee.',
          'Fill the fields (see Fields). Fields with * are required.',
          'Click Save. A green message "Employee created" confirms it. The status is set to Active.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'User ID', help: 'The number (ID) of an existing user login. Shown only when adding. One user can have only one employee record.', required: true },
          { name: 'Department', help: 'Choose from the list.', required: true },
          { name: 'Employee Code', help: 'Unique code, e.g. EMP-010. Cannot be changed later.', required: true },
          { name: 'Position', help: 'Designation, e.g. Draughtsman.' },
          { name: 'Joining Date', help: 'First working day. Cannot be changed later. Payroll counts days from this date.', required: true },
          { name: 'Current Salary', help: 'Monthly salary in rupees. For payroll use Pay Profiles.' },
          { name: 'Date of Birth', help: 'Date of birth.' },
          { name: 'Gender', help: 'Male, Female or Other.' },
          { name: 'Nationality', help: 'e.g. Indian.' },
          { name: 'Aadhaar Number', help: '12 digits. Not checked here, so type carefully.' },
          { name: 'PAN Number', help: '10 characters like ABCDE1234F. Not checked here, so type carefully.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'If the user already has an employee record, saving fails. Edit the existing record instead.',
          'On Edit, an empty box does not clear the old value.',
          'Delete removes the employee and all their records for ever. To mark someone who left, use Exit & clearance on the Employees screen.',
        ],
      },
    ],
    related: [
      { label: 'Employees', path: '/employees' },
      { label: 'Users', path: '/users' },
    ],
  },
  {
    path: '/departments',
    title: 'Departments',
    summary: 'The list of departments used for employees, job postings and reports.',
    sections: [
      {
        title: 'Step by step: add or rename a department',
        steps: [
          'Click Add Department, or the pencil (Edit Department) on a row.',
          'Enter the Department Name and, if you like, a Description.',
          'Click Save. A green message confirms it.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Department Name', help: 'Short name, e.g. Design, Drafting, Accounts. Check the list first — the same name can be entered twice.', required: true },
          { name: 'Description', help: 'What the department does. Optional.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'A department that still has employees cannot be deleted; the message says how many. Move them to another department first (Employees → Edit), or just rename it.',
          'A renamed department shows the new name everywhere at once.',
        ],
      },
    ],
    related: [{ label: 'Employees', path: '/employees' }],
  },
  {
    path: '/letters-certificates',
    title: 'Letters & Certificates',
    summary:
      'Issue HR letters and certificates (appointment, experience, relieving, salary certificate, warning, awards and more) and keep a register of every letter issued.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'The register: Reference, Issued, Employee, Department, Type, Subject, Issued by. Newest first.',
          'Search by employee, type or reference.',
          'The document icon (View / print) opens the letter. Click Print to print it on A4 with the company heading.',
        ],
      },
      {
        title: 'Step by step: issue a letter',
        steps: [
          'Click Issue letter.',
          'Choose the Employee and the Letter type. Check the Issued on date.',
          'If the letter needs extra details (for example New designation, Effective from and New salary for a Promotion Letter), fill them.',
          'Click Generate letter. The Subject and the Letter text appear.',
          'Read the text and correct it if needed. What you see is exactly what is issued.',
          'Click Issue letter. The letter gets a reference like SC/LTR/2026-09/0003 and opens for printing.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Employee', help: 'Active or in-active employee.', required: true },
          { name: 'Letter type', help: 'Appointment, Promotion, Service Certificate, Experience, NOC, Relieving, Spot Award, Employee of the Month / Quarter / Year, Warning Letter or Salary Certificate.', required: true },
          { name: 'Issued on', help: 'Date printed on the letter. Today by default. It also sets the month in the reference.', required: true },
          { name: 'Extra details', help: 'Asked only for some types, e.g. Purpose (NOC), Period and Citation (awards), Incident date, Incident and Expected conduct (Warning). A blank one prints as "______".' },
          { name: 'Subject', help: 'Filled by Generate letter. You can change it.' },
          { name: 'Letter', help: 'The full text. Edit freely before issuing.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Name, code, designation, department, dates and salary are taken from the employee record. Correct the record first if something is wrong, then Regenerate.',
          'If you change the employee or the date after generating, click Regenerate.',
          'An issued letter stays as it was, even after a promotion or salary change.',
          'Avoid deleting letters. Deleting one cannot be undone, and can make the next reference number clash.',
          'If you don’t see Issue letter or the Delete icon, your role doesn’t have the Create action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Employees', path: '/employees' },
      { label: 'Discipline Board', path: '/discipline-board' },
    ],
  },
  {
    path: '/discipline-board',
    title: 'Discipline Board',
    summary:
      'Anyone can send a complaint, query or feedback to HR here. HR and admin see all of them and record the action taken.',
    sections: [
      {
        title: 'Step by step: send feedback',
        steps: [
          'Type the subject in Enter subject.',
          'Type your message in Enter content (up to 1000 characters).',
          'Click Submit. A green message "Feedback shared successfully." confirms it.',
        ],
      },
      {
        title: 'Step by step: act on feedback (HR / admin)',
        steps: [
          'Under Active, click a row to read the Subject and message.',
          'Click Resolve when it is settled, or Take Verbal Action after a verbal warning. The item moves to Closed, labelled with the outcome.',
          'For a written warning, click Issue Warning Letter. Click Attach file and choose the signed letter (PDF, Word, Excel or PowerPoint). Click Save.',
          'Closed shows the date it was resolved or the warning letter was issued.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Feedback is not anonymous. HR sees your name and department.',
          'Only HR and admin see the list of all feedback.',
          'Issue Warning Letter only stores the file you attach. It is not emailed and not added to the Letters & Certificates register. To make the letter, use Letters & Certificates → Warning Letter, print it, then attach it here.',
        ],
      },
    ],
    related: [{ label: 'Letters & Certificates', path: '/letters-certificates' }],
  },
  {
    path: '/attendance-register',
    title: 'Attendance Register',
    summary:
      'Every employee’s attendance for one month, day by day. Correct a wrong day, or import punches from the biometric machine. Salary Processing uses these days.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Choose the month with the arrows or the Month and Year boxes. The register opens on the current month.',
          'One row per employee, one column per day. Weekly offs and holidays have a grey heading. Today’s column has a coloured border.',
          'Point at a cell to see the status, In and Out times, hours worked, Late, and any HR override.',
          'The columns at the end: P = present days (a half day adds 0.5), H = half days, PL = paid leave, UL = unpaid leave, A = absent, Late = late marks, LOP = loss-of-pay days.',
        ],
      },
      {
        title: 'Colours and letters',
        bullets: [
          'P green = Present. H amber = Half day. A red = Absent.',
          'PL blue = Paid leave. UL purple = Unpaid leave. ½L light blue = Half-day leave.',
          'WO grey = Weekly off. HO teal = Holiday. — = Not employed (before joining or after leaving).',
          'P… = In office today (punched in, not yet out). A blank cell = a day still to come.',
          'Orange dot (top right) = Late. Blue dot (bottom right) = Set by HR (the status was overridden).',
        ],
      },
      {
        title: 'How a day is decided',
        bullets: [
          'Worked 8 hours or more (punch out − punch in) = Present. 4 to 8 hours = Half day. Less than 4 hours = Absent.',
          'A past day with a punch in but no punch out = Half day. A past working day with no punch and no approved leave = Absent.',
          'Punch in after 09:45 (office start 09:30 + 15 minutes grace) = Late. Every 3 late marks in a month = half a day LOP.',
          'Approved leave shows as PL, UL or ½L. Paid holidays and weekly offs are paid days without attendance.',
          'LOP = Absent + Unpaid leave + late-mark LOP. Salary Processing cuts pay for these days.',
          'These numbers are the standard rules. HR can change them on Payroll Rules.',
        ],
      },
      {
        title: 'Step by step: correct a day',
        steps: [
          'Click the employee’s cell for that day. The CORRECT ATTENDANCE box opens.',
          'Enter the In time and Out time (24-hour, e.g. 09:30 and 18:30). Or leave them and choose a Status override.',
          'Write the reason in Remarks, e.g. "Site visit, forgot to punch".',
          'Click Save. A green message "Attendance saved." appears and the register reloads.',
        ],
        fields: [
          { name: 'In time', help: 'Punch in, 24-hour HH:MM. Required if you enter an Out time.' },
          { name: 'Out time', help: 'Punch out, 24-hour HH:MM. Must be after the In time.' },
          { name: 'Status override', help: 'Present, Half day or Absent. This wins over the punch times. Choose "— From punch times —" to let the times and rules decide.' },
          { name: 'Remarks', help: 'Why the day was corrected. Up to 255 characters.' },
        ],
      },
      {
        title: 'Step by step: import the biometric CSV',
        steps: [
          'Export the punches from the biometric machine as a CSV file.',
          'Make sure the columns are, in this order: employee_code, date, in_time, out_time. The first line must be the heading line.',
          'Click Import biometric CSV, then Choose CSV file, then Import.',
          'Read the result: "Imported X, skipped Y, Z problems." Each problem shows its line number and reason. Correct those lines and import again.',
          'Click Done.',
        ],
        bullets: [
          'Example lines: SDLLP-03,2026-10-01,09:28,18:41 and SDLLP-05,01-10-2026,09:45,18:30.',
          'employee_code = the Employee ID shown in Employees (e.g. SDLLP-03).',
          'date = yyyy-MM-dd (2026-10-01) or dd-MM-yyyy (01-10-2026). dd/MM/yyyy also works.',
          'in_time and out_time = 24-hour HH:MM, e.g. 18:30 (not 6:30 PM). Leave out_time blank if there was no out-punch.',
          'A row with no in time is skipped.',
          'A day already in the register gets the new times. A day an HR person overrode keeps its status and remarks.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Once a month’s salaries are marked paid, its attendance is locked. Saving or importing then shows "Salaries for that month are already paid". An admin can Reopen the month in Salary Processing.',
          'Any correction or import makes a calculated, unpaid month "out of date" in Salary Processing. Recalculate there.',
          'An override does not change a weekly off, holiday or full-day leave. Change the leave in Leave Approvals or the holiday in Holidays.',
          'For the current month, days still to come count in P until the month ends.',
          'If you can’t click cells or don’t see Import biometric CSV, your role doesn’t have the Edit action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Salary Processing', path: '/payroll' },
      { label: 'Leave Approvals', path: '/leave-approvals' },
      { label: 'Holidays', path: '/holidays' },
      { label: 'Payroll Rules', path: '/payroll-setup' },
    ],
  },
  {
    path: '/leave-approvals',
    title: 'Leave Approvals',
    summary: 'Approve or reject leave applications from staff, and add leave for an employee.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Active = applications waiting for a decision. Closed = decided; choose Approved or Rejected.',
          'Columns: Name, Department, Leave type, Start date and End date. Under each date you see (First Half), (Second Half) or (Full Day). Closed adds Status: Approved green, Rejected red.',
          'Click a row to see Number of days, Applied on and the Leave reason. Rejected rows also show the Reason for Rejection.',
        ],
      },
      {
        title: 'Step by step: approve or reject',
        steps: [
          'On Active, click the row and read the reason.',
          'Check the dates against the team’s work and the employee’s leave balance (Employees → open the employee → LEAVE BALANCE).',
          'Click Approve. A green message "Leave application approved." appears.',
          'Or click Reject, type the Rejection Reason (3 to 300 characters), and click Save.',
        ],
      },
      {
        title: 'Step by step: add leave for an employee',
        steps: [
          'Click + Add Leave for an employee.',
          'Choose the Employee and the Leave type.',
          'Choose the Start date and its session: Session 1 (First Half of the day), Session 2 (Second Half of the day) or Full Day.',
          'For more than one day, choose the End date and its session.',
          'Type the Reason. Click Save. This leave is approved at once.',
        ],
        fields: [
          { name: 'Employee', help: 'Only current employees are listed.', required: true },
          { name: 'Leave type', help: 'e.g. Casual Leave, Sick Leave.', required: true },
          { name: 'Start date + session', help: 'Today or later. Choose the session too.', required: true },
          { name: 'End date + session', help: 'Only for leave of more than one day. Must be after the start date.' },
          { name: 'Reason', help: '3 to 600 characters.', required: true },
          { name: 'Attach file', help: 'Optional, e.g. a medical certificate.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Approved leave shows on the Attendance Register as PL (paid), UL (unpaid) or ½L (half day), on working days only.',
          'Approving or changing leave makes a calculated, unpaid salary month "out of date". Recalculate in Salary Processing.',
          'Do not approve leave in a month whose salaries are already paid. The register changes, but the paid salary does not.',
          'The system does not stop you approving more leave than the balance. Check the balance first.',
          'A start date in the past is not allowed here. For a past day, correct the day in Attendance Register.',
          'If you don’t see Approve and Reject, your role doesn’t have the Approve action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Attendance Register', path: '/attendance-register' },
      { label: 'Employees', path: '/employees' },
      { label: 'Salary Processing', path: '/payroll' },
    ],
  },
  {
    path: '/holidays',
    title: 'Holidays',
    summary:
      'The company holiday calendar. Paid holidays are paid days off for everyone in attendance and payroll. Staff see this list in My Attendance.',
    sections: [
      {
        title: 'Step by step: add a holiday',
        steps: [
          'Choose the Year.',
          'Click Add holiday.',
          'Enter the Date and the Name. Choose the Type. Keep Paid holiday on for a normal holiday.',
          'Click Save. A green message "Holiday saved." appears.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Date', help: 'The holiday date. Only one holiday per date.', required: true },
          { name: 'Name', help: 'e.g. Diwali (Lakshmi Puja). Up to 150 characters.', required: true },
          { name: 'Type', help: 'National, Festival, Company or Optional.', required: true },
          { name: 'Paid holiday', help: 'On = a paid day off for everyone (HO in the register). Off = the day stays a normal working day.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Only paid holidays that are not Optional become HO for everyone. An Optional holiday is a normal working day; staff who want it apply for leave.',
          'A holiday on a weekly off still shows as HO.',
          'Adding, changing or deleting a holiday makes a calculated, unpaid salary month "out of date". Recalculate in Salary Processing.',
          'You cannot add, move or delete a holiday in a month whose salaries are already paid.',
          'Use the pencil (Edit) to correct a holiday, the red bin (Delete) to remove it.',
          'If you see "Read only" at the top, your role can view the holidays but not change them — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Attendance Register', path: '/attendance-register' },
      { label: 'Payroll Rules', path: '/payroll-setup' },
    ],
  },
  // ── Recruitment ────────────────────────────────────────────────────────────
  {
    path: ['/hiring-request', '/hiring-request/new', '/hiring-request/:jobPostingId'],
    title: 'Job Postings (Hiring Requests)',
    summary:
      'Post a job opening, open or close it on the careers page, and see how many candidates applied.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'The Hiring Requests table: Posting Date, Designation, Department, No. of Positions, No. of candidates applied, Status and More Details.',
          'Status is a switch: Closed on the left, Open on the right. Only Open jobs are shown on the website careers page.',
          'Click view in More Details to see the full posting below the table, with Edit and Delete buttons.',
        ],
      },
      {
        title: 'Step by step: post a new job',
        steps: [
          'Click + Hiring Request (top right). The New Hiring Request form opens below the table.',
          'Fill every field (see Fields). In Roles and Responsibilities, write one responsibility per line.',
          'Click Post. A green message confirms it. The new job is Open at once.',
          'To stop taking applications, turn the Status switch to Closed. It saves immediately.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Designation', help: 'Job title, e.g. Structural Engineer. 3 to 50 characters.', required: true },
          { name: 'Department', help: 'Choose from the list.', required: true },
          { name: 'Work Arrangement', help: 'e.g. In Office, On Site, Remote.', required: true },
          { name: 'Employment Type', help: 'e.g. Full-Time, Part-Time, Contract, Intern.', required: true },
          { name: 'Minimum Age / Maximum Age', help: 'Age group in years. At least 18. Maximum must be more than minimum.', required: true },
          { name: 'Gender Preferred', help: 'Any, Male, Female or Other.', required: true },
          { name: 'Qualification Preferred', help: 'e.g. B.Tech / B.E / Diploma in Civil Engineering.', required: true },
          { name: 'Roles and Responsibilities', help: 'One per line; each line shows as a bullet. 3 to 1000 characters.', required: true },
          { name: 'Tools Required', help: 'Choose from the list or type a new tool and press Enter. At least one.', required: true },
          { name: 'Minimum Years / Maximum Years', help: 'Experience required, in years. 0 or more. Maximum must be more than minimum.', required: true },
          { name: 'Minimum amount / Maximum amount', help: 'CTC to offer, in rupees. More than ₹0. Maximum must be more than minimum.', required: true },
          { name: 'Number of positions', help: '1 to 10.', required: true },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Edit opens the same form with Update instead of Post. Check every field again before you click Update.',
          'A job that has candidates cannot be deleted; the message says how many applied. Use the Closed switch to stop applications. A job with no candidates can be deleted.',
          'If the page opens but the list does not load, your login role may not have HR access — ask the admin.',
        ],
      },
    ],
    related: [{ label: 'Candidates', path: '/candidates' }],
  },
  {
    path: ['/candidates', '/candidates/:applicationId'],
    title: 'Candidates',
    summary:
      'Everyone who applied for a job: approve them for interview, reject them, or continue their interview process.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Three tabs: Applied (new applications), Scheduled (interview in progress) and Rejected. Hired candidates leave this board and appear in Employees.',
          'Columns: Application Date, Name, Contact, Email, Department/Designation, Status and More details.',
          'Status shows Candidate (orange) on Applied, the current round in green with the next interview date on Scheduled, and Rejected (red) with the round reached on Rejected.',
          'Click view to open the candidate’s details below the table: experience, salary, tools known, Resume and Additional file.',
        ],
      },
      {
        title: 'Step by step: approve or reject a new candidate',
        steps: [
          'Open the Applied tab and click view on the candidate.',
          'Read the details and open the Resume.',
          'To call them for interview, click Approve. Choose the interview date and time. Click Save. The candidate moves to Scheduled.',
          'To reject, click Reject. Type the Reason for rejection (3 to 300 characters). Click Save. The candidate moves to Rejected.',
          'Tell the candidate the interview date yourself (phone or email). The system does not send an invitation.',
        ],
      },
      {
        title: 'Step by step: a scheduled candidate',
        steps: [
          'Open the Scheduled tab and click view.',
          'To change the date, click Reschedule Interview, choose the new date and time, and click Save.',
          'Click Start interview process (or Resume interview process) to open the interview rounds.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'The interview date cannot be in the past.',
          'A rejected candidate shows the reason and the round they reached. They cannot be moved back from this screen.',
          '+ Hiring Request opens a new job posting form in a new browser tab.',
        ],
      },
    ],
    related: [
      { label: 'Job Postings', path: '/hiring-request' },
      { label: 'Employees', path: '/employees' },
    ],
  },
  {
    path: '/candidates/:applicationId/interview',
    title: 'Interview Rounds',
    summary:
      'Take a candidate through the 8 steps: three interview rounds, offer letter, on-boarding, documents, contract and induction. The last step creates the employee and the login.',
    sections: [
      {
        title: 'The steps',
        bullets: [
          'Round 1 (Aptitude): score Integrated Reasoning, Quantitative Reasoning and Verbal Reasoning, each out of 20, with a remark for each.',
          'Round 2 (Manager): score Technical knowledge, Team player, Problem solving skills and People skills, each out of 20, with remarks.',
          'Round 3 (HR): enter Salary Finalized. The table shows salary per month, per annum, PT, TDS, in-hand and per hour. Then Generate & Send Offer Letter.',
          'Offer Letter: shows whether the candidate accepted, rejected, or has not answered. The candidate has 48 hours to answer.',
          'On-boarding Process: personal details — photo, full name, date of birth, gender, contact, email, addresses, nationality, medical condition, Aadhaar and PAN.',
          'Document Collection: upload the candidate’s documents and approve or reject Background Verification.',
          'Contract: enter Employee Date of Joining and click Generate & Send.',
          'Induction: tick the joining items, generate the Employee ID and password, and complete the recruitment.',
          'Done steps can be opened (read only) by clicking them in the stepper. Future steps cannot be opened.',
        ],
      },
      {
        title: 'Step by step: a scored round (Round 1 or 2)',
        steps: [
          'Enter a score (0 to 20) and a remark (up to 100 characters) for each line.',
          'Enter the final remark next to Total Score.',
          'To pass the candidate, click Approve, choose the next interview date and time, and click Save.',
          'To stop, click Reject, type the Reason for rejection, and click Save.',
          'After saving you return to the Candidates list (Scheduled or Rejected tab).',
        ],
      },
      {
        title: 'Step by step: finish the hiring (Induction)',
        steps: [
          'Tick Add Biometrics, Welcome kit, Company policy kit and Helmet / Raincoat. All four are required.',
          'Add joining-kit documents with + Add document if needed.',
          'Click Generate next to Employee ID. Click view, then Copy, and keep the Employee ID and password safely.',
          'Click Submit & Complete Recruitment.',
          'The system creates the login (user name = Employee ID, role employee) and the employee record. The candidate leaves the Candidates board.',
          'Give the Employee ID and password to the new employee yourself.',
        ],
      },
      {
        title: 'On-boarding field rules',
        fields: [
          { name: 'Full name', help: 'Up to 100 characters.', required: true },
          { name: 'Date of birth', help: 'Not in the future. Age must be 18 to 65.', required: true },
          { name: 'Gender', help: 'Male, Female or Other.', required: true },
          { name: 'Contact number', help: '10 digits.', required: true },
          { name: 'Email ID', help: 'The candidate’s email.', required: true },
          { name: 'Residential address / Permanent address', help: 'Full address.', required: true },
          { name: 'Nationality', help: 'e.g. Indian.', required: true },
          { name: 'Aadhaar number', help: '12 digits. Cannot start with 0 or 1.' },
          { name: 'PAN number', help: '10 characters like ABCDE1234F.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Share form to candidate lets the candidate fill their own details. Send them the link yourself: the address is /data-entry/ followed by the application number.',
          'The candidate answers the offer on the page /offer/ followed by the application number. No login is needed. Send them the link yourself.',
          'The password is shown only before you submit. Copy it first. Later, only a password reset (Employees screen) can give a new one.',
          'After hiring, check the new employee’s department, joining date and salary in Employees and Pay Profiles. The joining date is set to the day you submit, and the salary from the candidate’s expected salary.',
          'Reject candidate is available at every step until the hiring is complete.',
        ],
      },
    ],
    related: [
      { label: 'Candidates', path: '/candidates' },
      { label: 'Employees', path: '/employees' },
      { label: 'Pay Profiles', path: '/pay-profiles' },
    ],
  },
  // ── Payroll ────────────────────────────────────────────────────────────────
  {
    path: '/payroll',
    title: 'Salary Processing',
    summary:
      'Work out one month’s salary for every employee in one click, check it, mark it paid, and make payslips and bank files.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Choose the month with the arrows or the Month and Year boxes at the top right. The screen opens on last month, because salary is normally made after the month ends.',
          'The chip next to the month shows its state: Not calculated (grey), Calculated (blue), Calculated — out of date (orange) or Paid (green).',
          'Under the month you see who calculated it and when. For a paid month you also see the payment date, the reference and who marked it paid.',
          'The boxes show the month’s totals: Employees, Gross, Deductions, Net pay and Employer contributions.',
          'The table has one row per employee: Monthly gross, Pay days, LOP (loss of pay days, in red), Payable days, Earned gross, Deductions, Net pay and Payment (BANK, CHEQUE or CASH). "No bank account" in red means the pay profile has no account number.',
          'A warning line under a name (for example "No salary set for this month") means that employee has no salary revision for this month. Add one in Pay Profiles.',
          'Click any row to open the SALARY BREAKDOWN: the day counts, each earning and deduction, net pay and the employer’s share.',
        ],
      },
      {
        title: 'Step by step: pay a month',
        steps: [
          'First check the month’s attendance in Attendance Register and the leave in Leave Approvals. Check new joiners and salary changes in Pay Profiles.',
          'Choose the month. Click Calculate payroll (or Recalculate if it was calculated before).',
          'A green message shows the number of employees and the total net pay. Check the table. Click a row to see how a salary was worked out.',
          'Click Salary register Excel to check all numbers in a spreadsheet, if you want.',
          'When the numbers are right, the accounts team makes the payment in the bank.',
          'Click Mark salaries paid. Enter the Payment date and the Reference (UTR / cheque no. / batch). Click Mark paid.',
          'Click Payslips PDF to print or share the payslips. Click the small PDF icon in the Slip column for one employee’s payslip.',
        ],
      },
      {
        title: 'How the salary is worked out',
        bullets: [
          'Monthly gross comes from the employee’s salary revision in force on the last day of the month (Pay Profiles).',
          'Pay days is the basis set in Payroll Rules. The standard is CALENDAR: the days in the month (28 to 31). It can also be a fixed 30, or the working days only.',
          'LOP days = Absent days + Unpaid leave days + Late-mark LOP. Late-mark LOP: every 3 late marks in a month cost half a day (the number is a rule).',
          'Payable days = Pay days − days before joining or after leaving − LOP days.',
          'Earned gross = Monthly gross × Payable days ÷ Pay days.',
          'Standard earnings: Basic = 50% of gross. HRA = 40% of Basic. Special allowance = the balance, so that the earnings add up to the gross. All three are reduced for LOP days.',
          'Standard deductions: PF = 12% of the earned Basic, with Basic counted only up to ₹15,000 (so at most ₹1,800). ESI = 0.75% of the earned gross, only when the monthly gross is ₹21,000 or less. Professional tax from the Maharashtra slab (₹200 a month, ₹300 in February, for most staff). TDS = the monthly amount in the pay profile. Advance = salary advance paid to the employee during this month.',
          'Employer contributions (PF 12% and ESI 3.25%) are shown, but they are not cut from the employee’s pay.',
          'Net pay = Earned gross − Deductions, rounded to the nearest rupee. A deduction that comes to zero is not shown.',
          'PF, ESI and PT are applied only when they are switched on in the employee’s pay profile.',
        ],
      },
      {
        title: '"Out of date" and automatic recalculation',
        bullets: [
          'The month remembers every input it was calculated from: attendance, leave, holidays, salaries, pay profiles, payroll rules and advances.',
          'If any of these changes after calculation, the chip turns orange (Calculated — out of date) and a warning says: Recalculate. Click Recalculate.',
          'When you make the salary register, payslips, bank advice or bank statement for an out-of-date month, the system recalculates it first by itself. The screen then reloads with the new numbers.',
          'An out-of-date month cannot be marked paid. Recalculate first.',
        ],
      },
      {
        title: 'Reports and files',
        bullets: [
          'Salary register Excel — every employee with every day count and every component. For checking and for records.',
          'Payslips PDF — one page per employee, on the letterhead of the company set in Payroll Rules. The account number is partly hidden.',
          'Bank advice PDF — the letter to the bank with the list of bank-paid employees and amounts.',
          'Bank statement Excel — one row per employee paid by bank transfer, for the bank’s bulk-upload sheet. Employees paid by cheque or cash are not in it.',
          'A month that was never calculated has no reports. Calculate it first.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Who can do what: Calculate / Recalculate needs the Edit action. Mark salaries paid needs the Approve action (accounts). Payslips PDF, Bank advice PDF, Bank statement Excel and the Slip column need the Export action. If you don’t see a button, your role doesn’t have that action — ask the admin.',
          'A month can be calculated only after it has begun. You can recalculate an unpaid month as many times as you like.',
          'Once paid, the month is locked. Attendance, punches, biometric import and salary revisions for that month cannot change, and employees see the salary in My Salary.',
          'Only an admin sees Reopen on a paid month. Reopen takes it back to Calculated so it can be corrected and paid again. Employees stop seeing it in My Salary until it is paid again.',
          'Negative net pay is shown in red. It usually means a large advance or TDS. Check the breakdown before paying.',
        ],
      },
    ],
    related: [
      { label: 'Attendance Register', path: '/attendance-register' },
      { label: 'Pay Profiles', path: '/pay-profiles' },
      { label: 'Payroll Rules', path: '/payroll-setup' },
      { label: 'Leave Approvals', path: '/leave-approvals' },
    ],
  },
  {
    path: '/pay-profiles',
    title: 'Pay Profiles',
    summary:
      'Each employee’s monthly gross salary (with its history), PF / ESI / PT, monthly TDS and bank details. Salary Processing reads everything from here.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'One row per employee: Employee, Status (with joining and leaving date), Monthly gross, Since (the date that salary started), Statutory (PF, ESI, PT — blue when on), TDS / month, Payment and Actions.',
          'Monthly gross "Not set" in red: the employee has no salary. Salary Processing will pay them nothing. Add a revision.',
          '"Bank details incomplete" in red: the employee is paid by bank transfer but the account holder name, account number or IFSC is missing.',
          'Actions: the money icon opens Salary & revisions. The pencil opens Edit pay profile (an eye icon, View pay profile, if your role can only view).',
        ],
      },
      {
        title: 'Step by step: give a salary or an increment',
        steps: [
          'Click the money icon (Salary & revisions) on the employee’s row.',
          'Under Add a revision, enter Effective from (the first day of the new salary).',
          'Enter Monthly gross (₹). Add a Note, for example "Annual increment".',
          'Click Add revision. The table at the top shows the full history.',
          'Old revisions stay. Earlier months keep the salary they had.',
        ],
      },
      {
        title: 'Step by step: PF, ESI, PT, TDS and bank',
        steps: [
          'Click the pencil (Edit pay profile) on the employee’s row.',
          'Switch PF, ESI and Professional tax on or off.',
          'Enter TDS per month, UAN and ESIC no. if they apply.',
          'Choose Paid by. For Bank transfer, fill Account holder name, Account number and IFSC.',
          'Click Save. A green message "Pay profile saved." confirms it.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Effective from', help: 'Date the new salary starts. Usually the 1st of a month. Only one revision per date.', required: true },
          { name: 'Monthly gross (₹)', help: 'Gross salary per month, before deductions (CTC less employer PF/ESI). Must be more than zero.', required: true },
          { name: 'Note', help: 'Why the salary changed, e.g. "Annual increment" or "Joining salary".' },
          { name: 'PF', help: 'On = 12% of Basic (Basic counted up to ₹15,000) is deducted, and the employer’s 12% is shown.' },
          { name: 'ESI', help: 'On = 0.75% of earned gross is deducted, but only in months when the monthly gross is ₹21,000 or less.' },
          { name: 'Professional tax', help: 'On = PT from the slab table (Maharashtra) is deducted.' },
          { name: 'TDS per month (₹)', help: 'Income tax to deduct every month. A number, zero or more. Leave blank for none.' },
          { name: 'UAN (PF)', help: 'The employee’s 12-digit PF UAN. Printed on the payslip.' },
          { name: 'ESIC no.', help: 'The employee’s ESIC insurance number. Printed on the payslip.' },
          { name: 'Paid by', help: 'Bank transfer, Cheque or Cash. Only Bank transfer employees go in the bank statement and bank advice.', required: true },
          { name: 'Account holder name', help: 'Name exactly as in the bank. If blank, the employee’s name is used.' },
          { name: 'Account number', help: 'Letters and digits only, no spaces, 6 to 40 characters. Required for Bank transfer.' },
          { name: 'IFSC', help: '11 characters: 4 letters, then 0 (zero), then 6 letters or digits. Example: HDFC0001234. Required for Bank transfer.' },
          { name: 'Bank', help: 'Bank name, e.g. HDFC Bank.' },
          { name: 'Branch', help: 'Branch name.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'A month is paid on the revision in force on its last day. A revision from the 15th therefore counts for the whole month.',
          'To correct a wrong revision, delete it (red bin icon) and add it again. Two revisions cannot have the same date.',
          'You cannot add or delete a revision dated in a month that is already paid.',
          'Any change here makes unpaid calculated months "out of date". Recalculate in Salary Processing.',
          'If you see "Read only" at the top, your role can view pay profiles but not change them — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Salary Processing', path: '/payroll' },
      { label: 'Payroll Rules', path: '/payroll-setup' },
      { label: 'Employees', path: '/employees' },
    ],
  },
  {
    path: '/payroll-setup',
    title: 'Payroll Rules',
    summary:
      'The office rules, salary components, professional tax slabs and weekly offs that the salary calculation uses. Change these only when the company policy changes.',
    sections: [
      {
        title: 'Rules tab',
        body: 'Each rule shows its value and a short explanation. Change the values, then click Save (the number in brackets is how many you changed). Undo changes puts back the saved values.',
        fields: [
          { name: 'Office start time', help: 'HH:MM, e.g. 09:30. A punch in after this time plus the grace minutes is a late mark.' },
          { name: 'Office end time', help: 'HH:MM, e.g. 18:30. Shown to staff only; the day’s status comes from the hours worked.' },
          { name: 'Grace time for late coming (minutes)', help: 'Minutes after the start time before a punch in counts as late. Standard: 15.' },
          { name: 'Minimum hours for a full day', help: 'Hours from punch in to punch out at or above this make the day Present. Standard: 8.' },
          { name: 'Minimum hours for a half day', help: 'At or above this, but below a full day, is a Half day. Below it the day is Absent. Standard: 4. Cannot be more than the full-day hours.' },
          { name: 'A past day with no punch out counts as', help: 'PRESENT, HALF_DAY or ABSENT. Standard: HALF_DAY. HR can correct the day.' },
          { name: 'A past working day with no attendance counts as', help: 'ABSENT (standard) or PRESENT. Used when there is no punch and no approved leave.' },
          { name: 'Late marks that cost half a day’s pay', help: 'Every this many late marks in a month = 0.5 day loss of pay. Standard: 3. 0 turns the penalty off.' },
          { name: 'Pay days in a month', help: 'CALENDAR: per-day pay = gross ÷ days in the month (standard). FIXED_30: gross ÷ 30. WORKING_DAYS: gross ÷ working days.' },
          { name: 'Round net pay to the nearest (₹)', help: '1 = nearest rupee (standard), 10 = nearest ten rupees, 0 = no rounding.' },
          { name: 'Professional tax state', help: 'State code whose PT slabs apply, e.g. MH.' },
          { name: 'Company payslips are issued by', help: 'The Company Master id whose letterhead and bank appear on payslips and the bank advice.' },
        ],
      },
      {
        title: 'Salary components tab',
        body: 'Components are worked out in order: earnings first, then deductions, then employer contributions. Click Add component, or the pencil to edit one.',
        bullets: [
          'Standard earnings: Basic salary (BASIC) = 50% of monthly gross. House rent allowance (HRA) = 40% of BASIC. Special allowance (SPECIAL) = Balance of gross.',
          'Standard deductions: PF = 12% of BASIC, capped at ₹15,000. ESI = 0.75% of earnings if gross ≤ ₹21,000. PT = Professional tax slab. TDS = Employee’s monthly TDS. ADVANCE = Advance salary recovery.',
          'Standard employer contributions: PF_ER = 12% of BASIC capped at ₹15,000. ESI_ER = 3.25% of earnings if gross ≤ ₹21,000.',
          '"Calculated as" choices: % of monthly gross; % of another component; Balance of gross; Fixed amount; % of earnings (if gross ≤ threshold); Professional tax slab; Employee’s monthly TDS; Advance salary recovery.',
          'Prorated earnings are paid for payable days only. A deduction or employer share that is a percent of a component uses that component’s earned amount.',
        ],
      },
      {
        title: 'Component fields',
        fields: [
          { name: 'Code', help: 'Short code in capital letters, digits and _ only, up to 20 characters, e.g. BASIC, HRA, PF_EE. Must be unique.', required: true },
          { name: 'Name', help: 'Name printed on the payslip, e.g. House rent allowance.', required: true },
          { name: 'Type', help: 'Earning, Deduction or Employer contribution (shown, not deducted).', required: true },
          { name: 'Order', help: 'Lower numbers are worked out first. An earning that is a percent of another must have a higher order than its base.' },
          { name: 'Calculated as', help: 'The method. The boxes below change with it.', required: true },
          { name: 'Percent', help: 'For the percent methods, e.g. 50 for 50%.' },
          { name: 'Of component (code)', help: 'For "% of another component": the code of an earning, e.g. BASIC.' },
          { name: 'Cap (wage ceiling)', help: 'The base is limited to this amount first, e.g. 15000 for PF. Blank = no cap.' },
          { name: 'Only if monthly gross is at most', help: 'For "% of earnings", e.g. 21000 for ESI. Blank = always.' },
          { name: 'Amount per month', help: 'For "Fixed amount".' },
          { name: 'Applies to', help: 'Everyone, or only employees with PF / ESI / PT switched on in their pay profile.' },
          { name: 'Rounding', help: 'None (paise), Nearest rupee, or Up to the rupee.' },
          { name: 'Prorate by payable days', help: 'On = reduced for LOP days and days not employed.' },
          { name: 'Show on payslip', help: 'Off hides the line from payslips and the breakdown.' },
          { name: 'Active', help: 'Off stops using it without deleting it.' },
        ],
      },
      {
        title: 'PT slabs and Weekly offs tabs',
        bullets: [
          'PT slabs: the slab is chosen on the month’s earned gross. Fields: State code (e.g. MH), Gender (Any, Male, Female), Earned gross from, Up to (blank = no limit), PT per month, PT in February.',
          'Standard Maharashtra slabs: men ₹0 up to ₹7,500, ₹175 up to ₹10,000, ₹200 above (₹300 in February). Women ₹0 up to ₹25,000, ₹200 above (₹300 in February).',
          'Weekly offs: a weekly off is a paid day without attendance. Choose the Day and the Weeks of the month: ALL for every week, or e.g. 2,4 for the 2nd and 4th Saturday. The standard is Sunday, every week.',
          'Holidays are kept on their own screen (HR → Holidays).',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'A change here makes every unpaid calculated month "out of date". It recalculates before its next report. Paid months keep their figures.',
          'You cannot delete a component that other components are a percent of. Change those first, or switch it off with Active.',
          'If you see "Read only" at the top and no Save or Add buttons, your role can view the rules but not change them — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Salary Processing', path: '/payroll' },
      { label: 'Holidays', path: '/holidays' },
      { label: 'Attendance Register', path: '/attendance-register' },
    ],
  },
  {
    path: '/salary-board',
    title: 'Salary Board (old)',
    summary:
      'The older, hours-based salary sheet. New months are paid on Payroll → Salary Processing; use this screen only for old months or when told to.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'The month (it opens on last completed month) and a status chip: Unreleased, Unpaid, Requested (amber) or Paid (green).',
          'Totals: Month total, Employees and Awaiting HR sign-off (rows not yet verified).',
          'Columns: Employee, Department, Days, Payable hrs, OT, Rate/hr, Gross, Incentive, Advance, PT, TDS, Net payable and HR (Verified or Pending).',
          'Here salary = monthly salary × payable hours ÷ standard hours (scheduled days × 8.5). PT is ₹200 and TDS is 10%. Holidays are not counted by themselves; lower Scheduled days for them.',
        ],
      },
      {
        title: 'Step by step',
        steps: [
          'Choose the month. Click Open month to make one row per employee (or Recalculate month to work the hours out again).',
          'Click the pencil (Correct hours / verify) on a row. Correct the days and hours, add an Incentive (₹), switch on Verified by HR, and click Save.',
          'To pay an advance, click the money icon (Pay an advance). Enter Amount (₹), Paid on and Payment method. Click Record advance.',
          'When all rows are verified, click Release all salaries and confirm. The month goes to Accounts and payslips are written.',
          'Accounts marks it paid on Payments → Salary tab.',
        ],
      },
      {
        title: 'Fields (Correct hours)',
        fields: [
          { name: 'Scheduled days', help: 'Working days in the month. Lower it for a public holiday. Whole numbers only.' },
          { name: 'Days attended', help: 'Days the employee came. Whole numbers only.' },
          { name: 'Hours worked', help: 'Total hours worked in the month.' },
          { name: 'Overtime hours', help: 'Extra hours to pay.' },
          { name: 'Negative hours', help: 'Hours to cut. Subtracted from payable hours.' },
          { name: 'Null hours', help: 'Hours paid without work.' },
          { name: 'Incentive (₹)', help: 'Extra amount added to net pay.' },
          { name: 'Verified by HR', help: 'Switch on when you have checked the row.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Recalculate month works the hours out again for rows not yet Verified. Verified rows keep your corrected days and hours and are only re-priced. Incentive is kept.',
          'An advance is cut from the month of its Paid on date. The new Salary Processing also cuts it from that month.',
          'After Release, any edit re-prices the release until Accounts marks it paid. Once paid, nothing can change.',
          'Only HR and admin see the edit buttons. Accounts can only view.',
        ],
      },
    ],
    related: [
      { label: 'Salary Processing', path: '/payroll' },
      { label: 'Payments', path: '/payments' },
    ],
  },
  {
    path: '/reimbursement-approvals',
    title: 'Reimbursement',
    summary:
      'Approve or reject staff expense claims (vouchers): office supplies, travel, site visit, hotel and other expenses.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Active = claims waiting. Closed = decided; choose Approved or Rejected.',
          'Columns: Date (date of the expense), Name, Department, Request type and Amount. Closed adds Status: Approved green, Rejected red.',
          'Click a row to see Applied on and the employee’s Note.',
          'Request types: Office supplies, Gift voucher, Site visit, Hotel expenses, Travel expenses, Training and education expense, Dues and subscription expense, Utilities, Other.',
        ],
      },
      {
        title: 'Step by step: approve or reject',
        steps: [
          'On Active, click the row and read the Note. Check the bill or receipt with the employee.',
          'Click Approve. The full amount is approved. A green message "Voucher request approved" appears.',
          'Or click Reject, type the Rejection Reason (3 to 300 characters), and click Save.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
                    'You cannot approve part of a claim. Reject it with a reason, and the employee can claim the right amount again.',
          'An approved claim goes to Payments → Reimbursements for the accounts team to pay. Payment is not made on this screen.',
          'Staff make claims on My Reimbursements (+ Add Voucher).',
          'If you don’t see Approve and Reject, your role doesn’t have the Approve action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Payments', path: '/payments' },
      { label: 'Vouchers & Leaves (HR desk)', path: '/admin-module' },
    ],
  },
  // ── Billing ────────────────────────────────────────────────────────────────
  {
    path: '/bills',
    title: 'Bills & Invoices',
    summary:
      'Raise proforma bills and tax invoices from an approved quotation, record what the client paid, and cancel or convert bills.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Filters at the top: Financial year (opens on the current year), Company, Type (Bills & proformas, Bills, Proformas) and Search (bill no., client, job…).',
          'Columns: Bill no. (with "Proforma" or "Bill" under it), Date, Client, Job, Company, Amount (before GST), GST, Total, Due (bills only) and Status.',
          'Status colours: Unpaid (red), Part paid (orange), Paid (green), Open (blue, a live proforma), Converted to bill (grey) and Cancelled (grey, the row looks faded).',
          'The last row, Live bills shown, adds up Total and Due of the live bills on screen. Proformas and cancelled bills are not added.',
          'Click any row to open the bill. From there you can view the PDF, record a receipt, give a discount, convert or cancel.',
        ],
      },
      {
        title: 'Step by step: raise a bill or proforma',
        steps: [
          'Click New bill (tax invoice) or New proforma.',
          'In Approved quotation, search by job, client or company and choose the quotation.',
          'Tick the milestones to bill. The amount is filled from the milestone %. You can change the amount.',
          'Check Reference to and Bill date. Fill Payment request no./date and Work order no./date if you have them.',
          'Click Raise bill · ₹ … (or Raise proforma · ₹ …). The bill opens. Click View PDF to print or send it.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Approved quotation', help: 'Only approved quotations are listed. If none appears, approve the quotation on the job’s quotation list first.', required: true },
          { name: 'Milestones (tick boxes)', help: 'Tick at least one. Each ticked amount must be above zero. GST 18% (CGST 9% + SGST 9%) is added on top.', required: true },
          { name: 'Reference to', help: 'Printed under "Reference to", e.g. Proposed Residential Building at Kothrud. Filled from the job name and site; you can change it.' },
          { name: 'Bill date', help: 'Today by default. It cannot be a future date. The financial year of the bill comes from this date.', required: true },
          { name: 'Payment request no. / Payment request date', help: 'The client’s payment request reference, if any. Printed on the bill.' },
          { name: 'Work order no. / Work order date', help: 'The client’s work order reference, if any. Printed on the bill.' },
        ],
      },
      {
        title: 'Step by step: turn several proformas into one tax invoice',
        steps: [
          'On Bills, tick the box at the start of each open proforma to convert. Only proformas of the same job can be ticked together; the others are greyed out.',
          'A blue bar shows how many are selected and their total. Click Convert to tax invoice….',
          'The dialog lists every line of the ticked proformas. Change Amount on bill to bill more or less than the proforma asked for. The difference shows under the box; the undo arrow puts the proforma amount back.',
          'To deduct or add something, click Add adjustment line, type what it is (for example "Extra site visit" or "Less: rate revision") and the amount. Use a minus amount, such as -5000, for a deduction.',
          'Advances already received on the proformas are shown in a blue note. They move onto the bill as receipts by themselves, so do not deduct them again as a line.',
          'Check Bill total against Proforma total on the right, pick the Bill date, and click Raise tax invoice.',
          'The new bill opens. Each proforma now shows Converted to bill, and the bill says which proformas it was raised from.',
        ],
      },
      {
        title: 'Step by step: record an advance against a proforma',
        steps: [
          'Open the proforma from Bills and click Record advance (shown while the proforma is open and not fully received).',
          'Enter the Date, Amount received, any TDS deducted, the Mode and the UTR / cheque no., then click Save advance.',
          'The advance gets a receipt number like SCPL/PI/MOTE/3/2026-27/R1, and the proforma shows Advance received.',
          'Click the receipt number in the receipts list to open the printed Advance Receipt (with the GST it includes) to send to the client.',
          'When the proforma is converted to a tax invoice, the advance moves onto the invoice as a receipt, so the invoice\'s Due is already reduced.',
        ],
      },
      {
        title: 'Receipts, discount, convert and cancel',
        fields: [
          { name: 'Record receipt → Date', help: 'Date the money came. Not a future date.', required: true },
          { name: 'Amount received', help: 'Filled with the full due. Change it for a part payment.' },
          { name: 'TDS deducted', help: 'TDS the client cut. It counts as paid. Amount + TDS must be more than zero and not more than the due.' },
          { name: 'Mode', help: 'NEFT, RTGS, IMPS, UPI, CHEQUE, CASH or OTHER.' },
          { name: 'UTR / Cheque no.', help: 'Bank reference or cheque number.' },
          { name: 'Discount → Discount amount', help: 'Write-off on this bill. Saving replaces the old discount. Enter 0 to remove it. It cannot be more than the unpaid amount.' },
          { name: 'Cancel… → Reason', help: 'Why the bill is cancelled. Required; it is printed on the PDF.', required: true },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'A bill cannot be edited after it is raised. To correct it, cancel it (with a reason) and raise a new one.',
          'A bill with receipts cannot be cancelled. Delete the receipts first (bin icon in the receipts list), then cancel. Advances that came from proformas do not block it: they go back to their proforma.',
          'Advances on one proforma cannot add up to more than its total. A proforma holding an advance cannot be cancelled until the advance is deleted.',
          'The tax invoice\'s total cannot be less than the advances received on the proformas it is raised from.',
          'Advances count as collected money on the Billing dashboard. They are not outstanding: only tax invoices are.',
          'Convert to tax invoice… (on an open proforma, or for several ticked proformas) makes one new tax invoice with a new INV number. The proformas keep their PI numbers and show Converted to bill.',
          'Amounts are before GST. CGST and SGST are worked out again on the changed amounts, line by line.',
          'Every milestone on the proformas stays on the bill: you can change its amount but not remove it. To leave a milestone out, convert only the proformas you want.',
          'A milestone line needs an amount above zero. An adjustment line can be plus or minus, but the bill as a whole must come to more than zero.',
          'If a milestone line\'s amount is changed, its payment % is dropped from the bill, because it no longer matches.',
          'Proformas with the same milestone cannot be converted together, and proformas of different quotations cannot go on one bill.',
          'If a bill made from proformas is cancelled, all of them become Open again and can be converted again.',
          'A milestone can be on only one live bill. It can be on several proformas.',
          'Bill numbers look like SCPL/INV/MOTE/7/2025-26: company / PI or INV / client code / serial / financial year. Numbers are given by the system.',
          'The job must have a Company set. If not, you see a warning and cannot raise the bill — set it on the job first.',
          'Client name, address and GST no. are copied onto the bill when it is raised. Later changes to the client do not change old bills.',
          'A discount alone also shows the bill as Part paid until the due is zero.',
        ],
      },
    ],
    related: [
      { label: 'Outstanding', path: '/outstanding' },
      { label: 'Payments', path: '/payments' },
    ],
  },
  {
    path: '/outstanding',
    title: 'Outstanding',
    summary: 'How much each client (or each company) has been billed, has paid and still owes.',
    sections: [
      {
        title: 'What you see',
        bullets: [
          'Filters: Financial year, Company, and By client / By company to change the grouping.',
          'Tiles at the top: Billed, Paid (received + TDS), Discount, Cancelled and Due (in red).',
          'Table columns: Client (or Company), Bills, Billed, Received, TDS, Paid, Discount, Cancelled, Due. "—" means zero. The last row is the Total.',
          'Rows are sorted with the largest Due first.',
        ],
      },
      {
        title: 'Step by step: follow up a client',
        steps: [
          'Choose the Financial year and, if needed, the Company.',
          'Click a client row to open the list of their bills.',
          'Click a bill to open it. Record receipt, Discount or View PDF work here the same as on Bills & Invoices.',
          'The report refreshes after you save.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Only tax invoices count. Proformas are never in this report.',
          'Due per bill = Total − Received − TDS − Discount.',
          'Cancelled bills count only in the Cancelled column. They are still listed in the detail with Due 0.',
          'The financial year filter uses the bill date. There are no due dates or ageing (days overdue) on this screen.',
          'Bills without a client are grouped under "—".',
        ],
      },
    ],
    related: [
      { label: 'Bills & Invoices', path: '/bills' },
      { label: 'Payments', path: '/payments' },
    ],
  },
  {
    path: '/payments',
    title: 'Payments',
    summary:
      'Older money records: job credits, monthly salary payments, billing expenses and reimbursements paid out. Bills and client receipts are on Bills & Invoices.',
    sections: [
      {
        title: 'Tabs',
        bullets: [
          'Credits — one row per job: Job Code, Project, Client, Job Value, Requested, Credited and Balance. Read only. Receipts recorded on Bills & Invoices do not appear here.',
          'Salary — one row per month: Month, Employees, Total, Sheet (View), Proof (View), Status and a Mark paid button.',
          'Billings — expenses paid out: ID, Employee, Particulars, Amount, Method, Date, Status.',
          'Reimbursements — reimbursements paid to staff, with the same columns.',
        ],
      },
      {
        title: 'Step by step: add a payment (debit)',
        steps: [
          'Click New Debit.',
          'Choose the Debit Type and enter the Amount.',
          'Fill Particulars, Payment Method and Payment Date.',
          'Click Create. A green message "Debit entry created" confirms it.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Debit Type', help: 'Salary, Billings or Reimbursements. Starts on the tab you are on.', required: true },
          { name: 'Amount', help: 'Amount paid in rupees.', required: true },
          { name: 'Particulars', help: 'What the payment was for.' },
          { name: 'Payment Method', help: 'Free text, e.g. Bank Transfer, Cash, UPI.' },
          { name: 'Payment Date', help: 'Date paid. The financial year is taken from it (today if blank).' },
        ],
      },
      {
        title: 'Step by step: mark a salary month paid (Salary tab)',
        steps: [
          'Open the Salary tab and find the month.',
          'Click Mark paid.',
          'If you have one, click Attach payment proof (optional) and choose the file.',
          'Click Mark paid. The month is closed: HR can no longer change its attendance.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Salary in the new payroll is paid on Payroll → Salary Processing (Mark salaries paid). The Salary tab here belongs to the older Salary Board.',
          'A debit made here with Debit Type "Salary" does not show on the Salary tab.',
          'There is no edit or delete for a debit on this screen. Check the amount before you click Create.',
        ],
      },
    ],
    related: [
      { label: 'Bills & Invoices', path: '/bills' },
      { label: 'Salary Processing', path: '/payroll' },
      { label: 'Reimbursement', path: '/reimbursement-approvals' },
    ],
  },
];
